import { useEffect, useState, useRef } from 'react';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
} from '@floating-ui/react';

const TextSelectionMenu = ({ onReply }) => {
    const [visible, setVisible] = useState(false);
    const [selectedText, setSelectedText] = useState('');
    const [virtualElement, setVirtualElement] = useState(null);
    
    const { refs, floatingStyles, isPositioned } = useFloating({
        open: visible,
        onOpenChange: setVisible,
        middleware: [
            offset(10),
            flip(),
            shift({ padding: 8 }),
        ],
        whileElementsMounted: autoUpdate,
        placement: 'top',
        elements: {
            reference: virtualElement
        }
    });

    useEffect(() => {
        const handleSelection = () => {
            const selection = window.getSelection();
            const text = selection.toString().trim();

            if (text.length > 0) {
                // Check if the selection is within an api-message element
                const anchorNode = selection.anchorNode;
                const apiMessageElement = anchorNode?.parentElement?.closest('.api-message');
                
                // Only show menu if selection is within API/bot message
                if (apiMessageElement) {
                    const range = selection.getRangeAt(0);
                    const rect = range.getBoundingClientRect();

                    // Create a virtual element for the selection
                    const virtualEl = {
                        getBoundingClientRect: () => rect,
                        contextElement: document.body
                    };
                    
                    setVirtualElement(virtualEl);
                    setSelectedText(text);
                    setVisible(true);
                } else {
                    setVisible(false);
                    setSelectedText('');
                    setVirtualElement(null);
                }
            } else {
                setVisible(false);
                setSelectedText('');
                setVirtualElement(null);
            }
        };

        const handleClickOutside = (e) => {
            if (refs.floating.current && !refs.floating.current.contains(e.target)) {
                // Small delay to allow button click to register
                setTimeout(() => {
                    setVisible(false);
                    setSelectedText('');
                    setVirtualElement(null);
                }, 100);
            }
        };

        const handleScroll = () => {
            if (visible) {
                setVisible(false);
                setSelectedText('');
                setVirtualElement(null);
            }
        };

        const handleWheel = () => {
            if (visible) {
                setVisible(false);
                setSelectedText('');
                setVirtualElement(null);
            }
        };

        document.addEventListener('mouseup', handleSelection);
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchend', handleSelection);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('wheel', handleWheel, { passive: true });

        return () => {
            document.removeEventListener('mouseup', handleSelection);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchend', handleSelection);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('wheel', handleWheel);
        };
    }, [refs.floating, visible]);

    const handleReply = () => {
        if (onReply && selectedText) {
            onReply(selectedText);
            setVisible(false);
            setSelectedText('');
            setVirtualElement(null);
            window.getSelection().removeAllRanges();
        }
    };

    const handleCopy = async () => {
        if (selectedText) {
            try {
                await navigator.clipboard.writeText(selectedText);
                setVisible(false);
                setSelectedText('');
                setVirtualElement(null);
                window.getSelection().removeAllRanges();
            } catch (err) {
                console.error('Failed to copy text:', err);
            }
        }
    };

    const handleMenuMouseDown = (e) => {
        // Prevent mousedown from clearing the selection or triggering handleClickOutside
        e.preventDefault();
        e.stopPropagation();
    };

    if (!visible) return null;

    return (
        <div
            ref={refs.setFloating}
            className={`text-selection-menu ${isPositioned ? 'positioned' : ''}`}
            style={{
                ...floatingStyles,
                opacity: isPositioned ? 1 : 0,
                pointerEvents: isPositioned ? 'auto' : 'none'
            }}
            onMouseDown={handleMenuMouseDown}
        >
            <button
                className="text-selection-menu-item"
                onClick={handleReply}
            >
                Reply
            </button>
            <button
                className="text-selection-menu-item"
                onClick={handleCopy}
            >
                Copy
            </button>
        </div>
    );
};

export default TextSelectionMenu;
